import React, { useState, useRef, useEffect } from "react";
import { getPieceCounts, pieceNameMap, PieceCode } from "@/shared/utils/piecesCaptured";
import { useAppSocketContext } from "@/shared/hooks";
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { clearAbandonmentWarning, selectAbandonmentWarning } from '@/store/slices/uiSlice';
import { Move } from 'chess.js';
import '@/css/capture.css'

function ChessGameInfo({ game,lastLocalMove, gameOver, tournamentID, gameId, playerColor, whiteUsername, blackUsername, startTime }: { game: any, lastLocalMove: Move | null, gameOver: boolean, tournamentID: number, gameId: number, playerColor: string, whiteUsername: string, blackUsername: string, startTime: number}){
    const { incomingTime } = useAppSocketContext();
    const dispatch = useAppDispatch();
    const abandonmentWarning = useAppSelector(selectAbandonmentWarning);
    const [abandonmentCountdown, setAbandonmentCountdown] = useState(0);

    // Server-authoritative time (from clock_tick events)
    const [serverWhiteTimeMs, setServerWhiteTimeMs] = useState(startTime);
    const [serverBlackTimeMs, setServerBlackTimeMs] = useState(startTime);

    // Client-side display time (smoothly counts down between server updates)
    const [whiteTimeMs, setWhiteTimeMs] = useState(startTime);
    const [blackTimeMs, setBlackTimeMs] = useState(startTime);
    const [activePlayer, setActivePlayer] = useState<'white' | 'black'>('white');
    const [clockPhase, setClockPhase] = useState<string>('pre_game_no_show');

    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [lastMoveColor, setLastMoveColor] = useState<'w' | 'b' | null>(null);
    const [moveCount, setMoveCount] = useState(0);

    const [chunkedMoves, setChunkedMoves] = useState<string[][]>([]);
    const [capturedWhitePieces, setCapturedWhitePieces] = useState<string[]>([]);
    const [capturedBlackPieces, setCapturedBlackPieces] = useState<string[]>([]);
    const whiteCounts = getPieceCounts(capturedWhitePieces as PieceCode[]);
    const blackCounts = getPieceCounts(capturedBlackPieces as PieceCode[]);

    // Initial HTTP fetch for clock state (fallback if WebSocket not connected)
    useEffect(() => {
      const fetchInitialTimes = async () => {
        try {
          const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/fetchClockTimes?tournamentId=${tournamentID}&gameId=${gameId}`);
          const data = await res.json();
          setServerWhiteTimeMs(data.whiteTimeMs);
          setServerBlackTimeMs(data.blackTimeMs);
          setWhiteTimeMs(data.whiteTimeMs);
          setBlackTimeMs(data.blackTimeMs);
          if (data.activePlayer === 'white' || data.activePlayer === 'black') {
            setActivePlayer(data.activePlayer);
          }
          if (data.clockPhase) {
            setClockPhase(data.clockPhase);
          }
          setInitialFetchDone(true);
        } catch (error) {
          console.error('[GameInfo] Failed to fetch initial clock times:', error);
        }
      };

      if (!initialFetchDone) {
        fetchInitialTimes();
      }
    }, [gameId, tournamentID, initialFetchDone]);

    // WebSocket clock_tick updates (server broadcasts every 1s) - server is source of truth
    useEffect(() => {
      if (
        incomingTime &&
        incomingTime.gameId === gameId &&
        incomingTime.whiteTimeMs !== undefined &&
        incomingTime.blackTimeMs !== undefined
      ) {
        setServerWhiteTimeMs(incomingTime.whiteTimeMs);
        setServerBlackTimeMs(incomingTime.blackTimeMs);
        // Sync display time to server (corrects any client drift)
        setWhiteTimeMs(incomingTime.whiteTimeMs);
        setBlackTimeMs(incomingTime.blackTimeMs);
        if (incomingTime.activePlayer === 'white' || incomingTime.activePlayer === 'black') {
          setActivePlayer(incomingTime.activePlayer);
        }
        if (incomingTime.clockPhase) {
          setClockPhase(incomingTime.clockPhase);
        }
      }
    }, [incomingTime, gameId]);

    // Any transition into running phase means no-show abandonment warnings are no longer valid.
    useEffect(() => {
      if (clockPhase === 'running' && abandonmentWarning.gameId === gameId) {
        setAbandonmentCountdown(0);
        dispatch(clearAbandonmentWarning());
      }
    }, [clockPhase, abandonmentWarning.gameId, gameId, dispatch]);

    // Handle abandonment warning countdown
    useEffect(() => {
      if (abandonmentWarning.gameId === gameId && abandonmentWarning.secondsRemaining > 0) {
        // Show countdown 3 seconds ahead of backend timeout handling.
        setAbandonmentCountdown(Math.max(0, abandonmentWarning.secondsRemaining - 3));
      }
    }, [abandonmentWarning, gameId, playerColor]);

    // If the warned side makes a move, clear warning immediately.
    useEffect(() => {
      if (abandonmentWarning.gameId !== gameId || abandonmentCountdown <= 0 || !lastMoveColor) {
        return;
      }

      const warnedMoved =
        (abandonmentWarning.color === 'white' && lastMoveColor === 'w') ||
        (abandonmentWarning.color === 'black' && lastMoveColor === 'b');

      if (warnedMoved) {
        setAbandonmentCountdown(0);
        dispatch(clearAbandonmentWarning());
      }
    }, [abandonmentWarning, abandonmentCountdown, gameId, lastMoveColor, dispatch]);

    // Client-side abandonment countdown (1 second intervals)
    useEffect(() => {
      if (abandonmentCountdown <= 0) return;

      const interval = setInterval(() => {
        setAbandonmentCountdown(prev => Math.max(0, prev - 1));
      }, 1000);

      return () => clearInterval(interval);
    }, [abandonmentCountdown]);

    // Track whose turn it is and move count
    useEffect(() => {
      const history = game.history({ verbose: true });
      const count = history.length;
      setMoveCount(count);

      if (count > 0) {
        const lastMove = history[history.length - 1];
        setLastMoveColor(lastMove.color);
      } else {
        setLastMoveColor(null);
      }
    }, [game]);

    // Client-side smooth countdown (interpolation between server updates)
    // Only tick during running phase from backend clock state.
    useEffect(() => {
      if (gameOver) return;
      if (clockPhase !== 'running') {
        return;
      }

      const interval = setInterval(() => {
        if (activePlayer === 'white') {
          setWhiteTimeMs(prev => Math.max(0, prev - 100));
        } else if (activePlayer === 'black') {
          setBlackTimeMs(prev => Math.max(0, prev - 100));
        }
      }, 100); // Update every 100ms for smooth countdown

      return () => clearInterval(interval);
    }, [activePlayer, clockPhase, gameOver]); 
      
      useEffect(() => {
        const moves = game.history();
        const grouped: string[][] = [];
  
        for (let i = 0; i < moves.length; i += 2) {
          grouped.push([moves[i], moves[i + 1]]);
        }
  
        setChunkedMoves(grouped);
      }, [game, lastLocalMove]);

    useEffect(() => {
      const verboseMoves = game.history({ verbose: true });
      const whiteCaptures: string[] = [];
      const blackCaptures: string[] = [];

      for (const move of verboseMoves) {
        if (move.captured) {
          if (move.color === 'w') {
            blackCaptures.push(move.captured);
          } else {
            whiteCaptures.push(move.captured);
          }
        }
      }

      setCapturedWhitePieces(whiteCaptures);
      setCapturedBlackPieces(blackCaptures);
    }, [game, lastLocalMove]);


    // Format times for display
    const formattedWhiteTime = formatTime(whiteTimeMs);
    const formattedBlackTime = formatTime(blackTimeMs);

    // Low-time warning (< 10 seconds)
    const whiteLowTime = whiteTimeMs < 10000 && whiteTimeMs > 0;
    const blackLowTime = blackTimeMs < 10000 && blackTimeMs > 0;

    // Determine whose turn it is (whose clock is active)
    const isWhiteTurn = activePlayer === 'white';
    const isBlackTurn = activePlayer === 'black';

    // Determine which clock to grey out based on current turn
    const topClockInactive = playerColor === 'b' ? !isWhiteTurn : !isBlackTurn;
    const bottomClockInactive = playerColor === 'b' ? !isBlackTurn : !isWhiteTurn;

    // Check if abandonment warning should show on top clock (opponent's clock)
    const showTopAbandonmentWarning = abandonmentCountdown > 0 &&
                                       abandonmentWarning.gameId === gameId &&
                                       ((playerColor === 'b' && abandonmentWarning.color === 'white') ||
                                        (playerColor === 'w' && abandonmentWarning.color === 'black'));

    // Check if abandonment warning should show on bottom clock (player's clock)
    const showBottomAbandonmentWarning = abandonmentCountdown > 0 &&
                                          abandonmentWarning.gameId === gameId &&
                                          ((playerColor === 'w' && abandonmentWarning.color === 'white') ||
                                           (playerColor === 'b' && abandonmentWarning.color === 'black'));

    return(
    <div className='chessGameInfo'>
      <div className="player top">
        <div className="playerInfo">
          <div className="currentPoints">
            0
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div className="rank">
              #2
            </div>
            <div className="username">
              {playerColor == 'b' ? whiteUsername : blackUsername}
            </div>
            <div className='pic'>
              <svg width={15} viewBox="0 -0.5 1025 1025" className="icon" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M513.8875 0C231.1375 0 1.891667 229.25 1.891667 512c0 174.154167 86.9875 327.979167 219.8625 420.475 25.9375-90.3375 91.558333-158.35 180.8375-189.3375a310.0875 310.0875 0 0 1 10.029166-3.2875c0.954167-0.295833 1.904167-0.608333 2.866667-0.9a328.041667 328.041667 0 0 1 26.495833-6.741667c1.204167-0.254167 2.425-0.479167 3.6375-0.720833a332.75 332.75 0 0 1 24.3125-3.945833c1.379167-0.175 2.75-0.366667 4.133334-0.525a357.916667 357.916667 0 0 1 14.079166-1.358334h18.545834c1.770833 0.120833 3.658333 0.120833 5.55 0h1.654166c-130.470833 0-236.6625-103.670833-240.858333-233.120833-0.145833-0.091667-0.291667-0.166667-0.4375-0.258333-8.295833-155.104167 131.7875-250.454167 233.391667-248.5375 2.629167-0.0875 5.254167-0.2 7.904166-0.2 2.516667 0 5.004167 0.1125 7.495834 0.1875 29.391667-0.5 61.9375 7.083333 93.2125 21.8625a241.125 241.125 0 0 1 57.345833 37.083333c50.608333 43.075 87.366667 108.058333 83.004167 189.6l-0.195834 0.116667c-4.120833 129.520833-110.3375 233.2625-240.8625 233.2625h25.983334a356.083333 356.083333 0 0 1 14.058333 1.358333c1.4 0.158333 2.779167 0.35 4.170833 0.529167a309.825 309.825 0 0 1 14.358334 2.1125c3.3 0.558333 6.570833 1.166667 9.820833 1.816666 1.254167 0.25 2.5125 0.479167 3.754167 0.745834a327.791667 327.791667 0 0 1 26.379166 6.7125c1.033333 0.3125 2.054167 0.645833 3.0875 0.966666 3.283333 1.020833 6.5375 2.083333 9.7625 3.204167 89.25 30.9625 154.895833 98.954167 180.9125 189.270833 132.7875-92.504167 219.708333-246.279167 219.708334-420.370833C1025.891667 229.25 796.641667 0 513.8875 0z" fill="#F08E83"></path><path d="M754.754167 492.391667c-63.754167 39.791667-230.933333-17.7625-280.608334-105.491667-46.325 81.2625-124.820833 153.2375-201.1125 105.633333 4.195833 129.45 110.383333 233.120833 240.858334 233.120834 130.520833 0 236.7375-103.741667 240.8625-233.2625zM614.6 265.591667a260.729167 260.729167 0 0 1 57.345833 37.083333 241.158333 241.158333 0 0 0-57.345833-37.083333zM521.3875 243.729167c-2.495833-0.075-4.979167-0.1875-7.495833-0.1875-2.65 0-5.275 0.1125-7.904167 0.2 2.533333 0.05 5.066667 0.104167 7.554167 0.275 2.575-0.179167 5.208333-0.245833 7.845833-0.2875z" fill="#FCE9EA"></path><path d="M441.979167 732.204167c1.204167-0.254167 2.425-0.479167 3.6375-0.720834-1.2125 0.241667-2.433333 0.470833-3.6375 0.720834zM455.566667 729.645833zM553.929167 727.0125c1.4 0.158333 2.779167 0.35 4.170833 0.529167-1.391667-0.179167-2.770833-0.370833-4.170833-0.529167zM567.941667 728.916667zM412.616667 739.845833c0.954167-0.295833 1.904167-0.608333 2.866666-0.9-0.9625 0.291667-1.908333 0.604167-2.866666 0.9zM582.283333 731.470833c1.25 0.25 2.5125 0.483333 3.754167 0.745834-1.241667-0.266667-2.5-0.5-3.754167-0.745834zM612.416667 738.929167c1.033333 0.3125 2.054167 0.645833 3.0875 0.966666-1.029167-0.320833-2.05-0.654167-3.0875-0.966666zM625.266667 743.1l2.1375 0.758333a119.479167 119.479167 0 0 1-10.475 22.158334c17.145833 26.404167 12.841667 141.5375-12.925 123.341666l-45.116667-31.654166-45-31.654167 2.95-2.079167c-0.983333 0.025-1.9625 0.075-2.95 0.075-1.029167 0-2.045833-0.054167-3.070833-0.079166l2.95 2.083333-45 31.654167-45.116667 31.654166c-25.9125 18.229167-30.204167-97.329167-12.883333-123.470833a119.583333 119.583333 0 0 1-10.379167-21.975c0.733333-0.2625 1.4625-0.520833 2.195833-0.775-89.279167 30.991667-154.9 99.004167-180.8375 189.3375 82.854167 57.679167 183.5375 91.525 292.1375 91.525 108.666667 0 209.404167-33.8875 292.291667-91.629167-26.0125-90.316667-91.658333-158.3125-180.908333-189.270833zM469.929167 727.5375c1.379167-0.175 2.75-0.366667 4.133333-0.525-1.3875 0.158333-2.754167 0.35-4.133333 0.525z" fill="#CFE07D"></path><path d="M410.770833 765.8875c3.441667-5.195833 7.733333-6.879167 12.883334-3.258333l45.116666 31.654166 42.045834 29.6875c1.025 0.025 2.041667 0.079167 3.070833 0.079167 0.9875 0 1.9625-0.05 2.95-0.075l42.054167-29.691667 45.116666-31.654166c5.175-3.654167 9.475-1.9125 12.925 3.3875a119.479167 119.479167 0 0 0 10.475-22.158334l-2.1375-0.758333a318.3625 318.3625 0 0 0-9.7625-3.204167c-1.033333-0.320833-2.054167-0.654167-3.0875-0.966666a327.791667 327.791667 0 0 0-26.379166-6.7125c-1.245833-0.2625-2.508333-0.495833-3.754167-0.745834a349.516667 349.516667 0 0 0-14.341667-2.554166 353.654167 353.654167 0 0 0-9.8375-1.375c-1.391667-0.179167-2.770833-0.370833-4.170833-0.529167a356.083333 356.083333 0 0 0-14.058333-1.358333h-27.6375c-1.891667 0.120833-3.779167 0.120833-5.55 0h-18.545834a357.916667 357.916667 0 0 0-14.079166 1.358333c-1.3875 0.158333-2.754167 0.35-4.133334 0.525a343.516667 343.516667 0 0 0-14.3625 2.108333c-3.341667 0.566667-6.658333 1.179167-9.95 1.8375-1.2125 0.241667-2.429167 0.466667-3.6375 0.720834a328.041667 328.041667 0 0 0-26.495833 6.741666c-0.9625 0.2875-1.908333 0.604167-2.866667 0.9a313.4875 313.4875 0 0 0-12.225 4.0625 119.333333 119.333333 0 0 0 10.375 21.979167z" fill="#FEFEFE"></path><path d="M604.008333 762.625l-45.116666 31.654167-42.054167 29.691666-2.95 2.079167 45 31.654167 45.116667 31.654166c25.766667 18.195833 30.070833-96.9375 12.925-123.341666-3.445833-5.304167-7.745833-7.045833-12.920834-3.391667zM423.654167 762.625c-5.15-3.620833-9.441667-1.9375-12.883334 3.258333-17.320833 26.1375-13.029167 141.7 12.883334 123.470834l45.116666-31.654167 45-31.654167-2.95-2.083333-42.045833-29.6875-45.120833-31.65z" fill="#7EA701"></path><path d="M474.145833 386.9c49.675 87.729167 216.854167 145.283333 280.608334 105.491667l0.195833-0.116667c4.3625-81.545833-32.391667-146.529167-83.004167-189.6a260.533333 260.533333 0 0 0-57.345833-37.083333c-31.275-14.779167-63.820833-22.3625-93.2125-21.8625-2.6375 0.045833-5.270833 0.108333-7.85 0.283333 8.341667 29.533333-8.270833 88.3-39.391667 142.8875z" fill="#F7B970"></path><path d="M474.145833 386.9c31.120833-54.5875 47.733333-113.354167 39.391667-142.8875a155.220833 155.220833 0 0 0-7.554167-0.275c-101.6-1.916667-241.683333 93.433333-233.391666 248.5375 0.145833 0.091667 0.291667 0.166667 0.4375 0.258333 76.295833 47.604167 154.791667-24.370833 201.116666-105.633333z" fill="#FBCE77"></path></g></svg>                    
            </div>
          </div>
        </div>

          <div className="clockContainer">
            <div className={`clockTime ${playerColor === 'b' && whiteLowTime ? 'lowTime' : playerColor === 'w' && blackLowTime ? 'lowTime' : ''}`} style={{ opacity: topClockInactive ? 0.4 : 1 }}>
              { playerColor === 'b' ? formattedWhiteTime : formattedBlackTime }
            </div>
          </div>
          <div className={`capturedPieces top${Object.keys(blackCounts).length ? ' white' : ''}`}>
            {Object.entries(whiteCounts).map(([piece, count]) => {
              const name = pieceNameMap[piece as PieceCode];
              const className =
                count > 1 && name !== "queen"
                  ? `captured-${count}w${name}`
                  : `captured-1w${name}`;
              return (
                <div
                  key={`w-${piece}`}
                  className={`capturedPiecesSpriteSheet ${className}`}
                ></div>
              );
            })}
          </div>
      </div>

      {/* Abandonment warning banner - shows between top player and move history when top player needs to move */}
      {showTopAbandonmentWarning && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #dc2626',
          borderRadius: '4px',
          padding: '8px 12px',
          margin: '8px 0',
          textAlign: 'center',
          color: '#991b1b',
          fontWeight: 'bold',
          fontSize: '13px'
        }}>
          ⚠️ Make a move in {abandonmentCountdown}s or forfeit!
        </div>
      )}

      <div className="moveHistoryContainer">
        {chunkedMoves.map((row, index) => (
          <div className="row" key={index} style={{ display: 'flex' }}>
            <div className="moveNumber">{index + 1}</div>
            <div style={{ gap: '4px', display: 'flex', width: '100%' }}>
              <div className={`moveNotation ${index % 2 === 0 ? 'dark' : ''}`}>{row[0] || ''}</div>
              <div className={`moveNotation ${index % 2 !== 0 ? 'dark' : ''}`}>{row[1] || ''}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Abandonment warning banner - shows between move history and bottom player when bottom player needs to move */}
      {showBottomAbandonmentWarning && (
        <div style={{
          backgroundColor: '#fee2e2',
          border: '2px solid #dc2626',
          borderRadius: '4px',
          padding: '8px 12px',
          margin: '8px 0',
          textAlign: 'center',
          color: '#991b1b',
          fontWeight: 'bold',
          fontSize: '13px'
        }}>
          ⚠️ Make a move in {abandonmentCountdown}s or forfeit!
        </div>
      )}

      <div className="player bottom">
      <div className={`capturedPieces bottom${Object.keys(blackCounts).length ? ' white' : ''}`}>
        {Object.entries(blackCounts).map(([piece, count]) => {
            const name = pieceNameMap[piece as PieceCode];
            const className =
              count > 1 && name !== "queen"
                ? `captured-${count}b${name}`
                : `captured-1b${name}`;
            return (
              <div
                key={`b-${piece}`}
                className={`capturedPiecesSpriteSheet ${className}`}
              ></div>
            );
          })}
      </div>
        <div className="clockContainer">
          <div className={`clockTime ${playerColor === 'b' && blackLowTime ? 'lowTime' : playerColor === 'w' && whiteLowTime ? 'lowTime' : ''}`} style={{ opacity: bottomClockInactive ? 0.4 : 1 }}>
          {playerColor === 'b' ? formattedBlackTime : formattedWhiteTime}
          </div>
        </div>
        <div className="playerInfo">
          <div className="currentPoints">
              0
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <div className="rank">
                #1
            </div>
            <div className="username">
              {playerColor == 'b' ? blackUsername : whiteUsername}
            </div>
            <div className='pic'>
              <svg width={15} viewBox="0 -0.5 1025 1025" className="icon" version="1.1" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><path d="M513.8875 0C231.1375 0 1.891667 229.25 1.891667 512c0 174.154167 86.9875 327.979167 219.8625 420.475 25.9375-90.3375 91.558333-158.35 180.8375-189.3375a310.0875 310.0875 0 0 1 10.029166-3.2875c0.954167-0.295833 1.904167-0.608333 2.866667-0.9a328.041667 328.041667 0 0 1 26.495833-6.741667c1.204167-0.254167 2.425-0.479167 3.6375-0.720833a332.75 332.75 0 0 1 24.3125-3.945833c1.379167-0.175 2.75-0.366667 4.133334-0.525a357.916667 357.916667 0 0 1 14.079166-1.358334h18.545834c1.770833 0.120833 3.658333 0.120833 5.55 0h1.654166c-130.470833 0-236.6625-103.670833-240.858333-233.120833-0.145833-0.091667-0.291667-0.166667-0.4375-0.258333-8.295833-155.104167 131.7875-250.454167 233.391667-248.5375 2.629167-0.0875 5.254167-0.2 7.904166-0.2 2.516667 0 5.004167 0.1125 7.495834 0.1875 29.391667-0.5 61.9375 7.083333 93.2125 21.8625a241.125 241.125 0 0 1 57.345833 37.083333c50.608333 43.075 87.366667 108.058333 83.004167 189.6l-0.195834 0.116667c-4.120833 129.520833-110.3375 233.2625-240.8625 233.2625h25.983334a356.083333 356.083333 0 0 1 14.058333 1.358333c1.4 0.158333 2.779167 0.35 4.170833 0.529167a309.825 309.825 0 0 1 14.358334 2.1125c3.3 0.558333 6.570833 1.166667 9.820833 1.816666 1.254167 0.25 2.5125 0.479167 3.754167 0.745834a327.791667 327.791667 0 0 1 26.379166 6.7125c1.033333 0.3125 2.054167 0.645833 3.0875 0.966666 3.283333 1.020833 6.5375 2.083333 9.7625 3.204167 89.25 30.9625 154.895833 98.954167 180.9125 189.270833 132.7875-92.504167 219.708333-246.279167 219.708334-420.370833C1025.891667 229.25 796.641667 0 513.8875 0z" fill="#F08E83"></path><path d="M754.754167 492.391667c-63.754167 39.791667-230.933333-17.7625-280.608334-105.491667-46.325 81.2625-124.820833 153.2375-201.1125 105.633333 4.195833 129.45 110.383333 233.120833 240.858334 233.120834 130.520833 0 236.7375-103.741667 240.8625-233.2625zM614.6 265.591667a260.729167 260.729167 0 0 1 57.345833 37.083333 241.158333 241.158333 0 0 0-57.345833-37.083333zM521.3875 243.729167c-2.495833-0.075-4.979167-0.1875-7.495833-0.1875-2.65 0-5.275 0.1125-7.904167 0.2 2.533333 0.05 5.066667 0.104167 7.554167 0.275 2.575-0.179167 5.208333-0.245833 7.845833-0.2875z" fill="#FCE9EA"></path><path d="M441.979167 732.204167c1.204167-0.254167 2.425-0.479167 3.6375-0.720834-1.2125 0.241667-2.433333 0.470833-3.6375 0.720834zM455.566667 729.645833zM553.929167 727.0125c1.4 0.158333 2.779167 0.35 4.170833 0.529167-1.391667-0.179167-2.770833-0.370833-4.170833-0.529167zM567.941667 728.916667zM412.616667 739.845833c0.954167-0.295833 1.904167-0.608333 2.866666-0.9-0.9625 0.291667-1.908333 0.604167-2.866666 0.9zM582.283333 731.470833c1.25 0.25 2.5125 0.483333 3.754167 0.745834-1.241667-0.266667-2.5-0.5-3.754167-0.745834zM612.416667 738.929167c1.033333 0.3125 2.054167 0.645833 3.0875 0.966666-1.029167-0.320833-2.05-0.654167-3.0875-0.966666zM625.266667 743.1l2.1375 0.758333a119.479167 119.479167 0 0 1-10.475 22.158334c17.145833 26.404167 12.841667 141.5375-12.925 123.341666l-45.116667-31.654166-45-31.654167 2.95-2.079167c-0.983333 0.025-1.9625 0.075-2.95 0.075-1.029167 0-2.045833-0.054167-3.070833-0.079166l2.95 2.083333-45 31.654167-45.116667 31.654166c-25.9125 18.229167-30.204167-97.329167-12.883333-123.470833a119.583333 119.583333 0 0 1-10.379167-21.975c0.733333-0.2625 1.4625-0.520833 2.195833-0.775-89.279167 30.991667-154.9 99.004167-180.8375 189.3375 82.854167 57.679167 183.5375 91.525 292.1375 91.525 108.666667 0 209.404167-33.8875 292.291667-91.629167-26.0125-90.316667-91.658333-158.3125-180.908333-189.270833zM469.929167 727.5375c1.379167-0.175 2.75-0.366667 4.133333-0.525-1.3875 0.158333-2.754167 0.35-4.133333 0.525z" fill="#CFE07D"></path><path d="M410.770833 765.8875c3.441667-5.195833 7.733333-6.879167 12.883334-3.258333l45.116666 31.654166 42.045834 29.6875c1.025 0.025 2.041667 0.079167 3.070833 0.079167 0.9875 0 1.9625-0.05 2.95-0.075l42.054167-29.691667 45.116666-31.654166c5.175-3.654167 9.475-1.9125 12.925 3.3875a119.479167 119.479167 0 0 0 10.475-22.158334l-2.1375-0.758333a318.3625 318.3625 0 0 0-9.7625-3.204167c-1.033333-0.320833-2.054167-0.654167-3.0875-0.966666a327.791667 327.791667 0 0 0-26.379166-6.7125c-1.245833-0.2625-2.508333-0.495833-3.754167-0.745834a349.516667 349.516667 0 0 0-14.341667-2.554166 353.654167 353.654167 0 0 0-9.8375-1.375c-1.391667-0.179167-2.770833-0.370833-4.170833-0.529167a356.083333 356.083333 0 0 0-14.058333-1.358333h-27.6375c-1.891667 0.120833-3.779167 0.120833-5.55 0h-18.545834a357.916667 357.916667 0 0 0-14.079166 1.358333c-1.3875 0.158333-2.754167 0.35-4.133334 0.525a343.516667 343.516667 0 0 0-14.3625 2.108333c-3.341667 0.566667-6.658333 1.179167-9.95 1.8375-1.2125 0.241667-2.429167 0.466667-3.6375 0.720834a328.041667 328.041667 0 0 0-26.495833 6.741666c-0.9625 0.2875-1.908333 0.604167-2.866667 0.9a313.4875 313.4875 0 0 0-12.225 4.0625 119.333333 119.333333 0 0 0 10.375 21.979167z" fill="#FEFEFE"></path><path d="M604.008333 762.625l-45.116666 31.654167-42.054167 29.691666-2.95 2.079167 45 31.654167 45.116667 31.654166c25.766667 18.195833 30.070833-96.9375 12.925-123.341666-3.445833-5.304167-7.745833-7.045833-12.920834-3.391667zM423.654167 762.625c-5.15-3.620833-9.441667-1.9375-12.883334 3.258333-17.320833 26.1375-13.029167 141.7 12.883334 123.470834l45.116666-31.654167 45-31.654167-2.95-2.083333-42.045833-29.6875-45.120833-31.65z" fill="#7EA701"></path><path d="M474.145833 386.9c49.675 87.729167 216.854167 145.283333 280.608334 105.491667l0.195833-0.116667c4.3625-81.545833-32.391667-146.529167-83.004167-189.6a260.533333 260.533333 0 0 0-57.345833-37.083333c-31.275-14.779167-63.820833-22.3625-93.2125-21.8625-2.6375 0.045833-5.270833 0.108333-7.85 0.283333 8.341667 29.533333-8.270833 88.3-39.391667 142.8875z" fill="#F7B970"></path><path d="M474.145833 386.9c31.120833-54.5875 47.733333-113.354167 39.391667-142.8875a155.220833 155.220833 0 0 0-7.554167-0.275c-101.6-1.916667-241.683333 93.433333-233.391666 248.5375 0.145833 0.091667 0.291667 0.166667 0.4375 0.258333 76.295833 47.604167 154.791667-24.370833 201.116666-105.633333z" fill="#FBCE77"></path></g></svg>                    
            </div>
          </div>
        </div>
      </div>
    </div>
   )};

   function formatTime(ms: number): string {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
  
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
   }

export default ChessGameInfo;
